import { Component, computed, inject, signal } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { finalize } from 'rxjs';
import { UiModule } from '../../../../components/ui.module';
import { EdgeDeviceService } from '../../../../service/edge-device.service';
import { NotifyService } from '../../../../service/notify.service';
import { EdgeDevice, EdgeDeviceActionResponse } from '../../../../types/edge-device';
import { EdgeDeviceNetworkManagementComponent } from '../../../components/edge-device-network-management/edge-device-network-management.component';

type CellParams = ICellRendererParams<EdgeDevice> & { deleteCallback: (element: EdgeDevice) => void };

@Component({
  selector: 'app-cell-installer-dashboard-shoppersense',
  imports: [UiModule, MatTooltipModule, EdgeDeviceNetworkManagementComponent],
  templateUrl: './cell-installer-dashboard-shoppersense.component.html',
  styleUrl: './cell-installer-dashboard-shoppersense.component.scss',
})
export class CellInstallerDashboardShoppersenseComponent implements ICellRendererAngularComp {
  public params!: CellParams;
  public actionInProgress = signal<'update' | 'reboot' | undefined>(undefined);
  public actionsLocked = signal(false);
  public actionsDisabled = computed(() => !!this.actionInProgress() || this.actionsLocked() || !this.params?.data?.serial_number);

  private edgeDeviceService = inject(EdgeDeviceService);
  private notifyService = inject(NotifyService);

  agInit(params: CellParams) {
    this.params = params;
  }

  refresh(params: CellParams) {
    const serialNumberChanged = this.params.data?.serial_number !== params.data?.serial_number;
    this.params = params;
    if (serialNumberChanged) {
      this.actionInProgress.set(undefined);
      this.actionsLocked.set(false);
    }
    return true;
  }

  public updateDevice(edgeDevice: EdgeDevice) {
    this.runAction(edgeDevice, 'update');
  }

  public rebootDevice(edgeDevice: EdgeDevice) {
    this.runAction(edgeDevice, 'reboot');
  }

  private runAction(edgeDevice: EdgeDevice, action: 'update' | 'reboot') {
    if (this.actionsDisabled() || !edgeDevice.serial_number) return;

    this.actionInProgress.set(action);
    const request =
      action === 'update'
        ? this.edgeDeviceService.updateDevice(edgeDevice.serial_number)
        : this.edgeDeviceService.rebootDevice(edgeDevice.serial_number);

    request.pipe(finalize(() => this.actionInProgress.set(undefined))).subscribe({
      next: (response) => this.handleResponse(action, response),
      // ServerErrorsInterceptor owns the single actionable error notification.
      error: () => undefined,
    });
  }

  private handleResponse(action: 'update' | 'reboot', response: EdgeDeviceActionResponse) {
    if (response.status === 'created') {
      this.actionsLocked.set(true);
      const actionLabel = action === 'update' ? 'Update' : 'Reboot';
      this.notifyService.notify(`${actionLabel} Submitted: Your ${action} has been successfully submitted and is now being processed.`);
      return;
    }

    const updateIsCurrent =
      action === 'update' &&
      (response.message?.toLowerCase().includes('latest') ||
        (!!response.current_version && !!response.target_version && response.current_version === response.target_version));
    if (updateIsCurrent) {
      this.notifyService.notify('Up to Date: This device is already up to date.');
      return;
    }

    this.notifyService.notify('Job already in Progress: A job is already queued and will be processed shortly. Please try again soon.');
  }
}
